import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Delete,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { GET_DISEASES, DELETE_DISEASE } from '../graphql/operations';
import { Disease } from '../types';
import { formatDateString } from '../utils/dateUtils';

const DiseaseList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDisease, setSelectedDisease] = useState<Disease | null>(null);

  const { data, loading, error, refetch, fetchMore } = useQuery(GET_DISEASES, {
    variables: { limit: 80 }
  });

  const [deleteDisease] = useMutation(DELETE_DISEASE, {
    onCompleted: () => {
      setDeleteDialogOpen(false);
      setSelectedDisease(null);
      refetch();
    },
    onError: (error) => {
      console.error('Error deleting disease:', error);
    }
  });



  const handleDeleteClick = (disease: Disease) => {
    setSelectedDisease(disease);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedDisease) {
      deleteDisease({
        variables: { id: selectedDisease.id }
      });
    }
  };

  const handleLoadMore = () => {
    if (data?.listDiseases?.nextToken) {
      fetchMore({
        variables: {
          nextToken: data.listDiseases.nextToken,
          limit: 80
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            listDiseases: {
              ...fetchMoreResult.listDiseases,
              items: [...prev.listDiseases.items, ...fetchMoreResult.listDiseases.items]
            }
          };
        }
      });
    }
  };

  if (loading && !data) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading diseases: {error.message}</Alert>;

  const diseases = data?.listDiseases?.items || [];
  const hasNextPage = !!data?.listDiseases?.nextToken;
  const loadingMore = loading && !!data;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Diseases ({diseases.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/diseases/new')}
        >
          Add New Disease
        </Button>
      </Box>

      <Grid container spacing={3}>
        {diseases.map((disease: Disease) => (
          <Grid item xs={12} sm={6} md={4} key={disease.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
              onClick={() => navigate(`/diseases/${disease.id}`)}
            >
              {disease.thumbnail_url && (
                <CardMedia
                  component="img"
                  height="250"
                  image={disease.thumbnail_url}
                  alt={disease.name}
                  sx={{ 
                    objectFit: 'cover',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    {disease.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={disease.is_active ? 'Active' : 'Inactive'}
                      color={disease.is_active ? 'success' : 'default'}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(disease);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
                
                {disease.icd10_code && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ICD-10: {disease.icd10_code}
                  </Typography>
                )}
                
                {disease.short_description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {disease.short_description}
                  </Typography>
                )}
                
                <Typography variant="caption" color="text.secondary">
                  Updated: {formatDateString(disease.updated_at)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {hasNextPage && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={20} /> : null}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}

      {diseases.length === 0 && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No diseases found
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/diseases/new')}
          >
            Create your first disease
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedDisease?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiseaseList;
